import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../../../providers/ThemeProvider";
import { getChartColors } from "../../../theme/chartTokens";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  Chart as ChartInstance,
  TooltipModel,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { IMetaDailyMetric } from "../interface/metaAds";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface PerformanceChartProps {
  data: IMetaDailyMetric[];
  height?: number | string;
  className?: string;
  currency?: string;
  currencySymbol?: string;
}

// ── Tooltip helpers ───────────────────────────────────────────────────────────

function tooltipRow(label: string, value: string, color: string, bold = false) {
  return `<div style="display:flex;justify-content:space-between;align-items:center;gap:20px;min-width:180px">
    <span style="font-size:11px;color:${color};${bold ? "font-weight:700" : "font-weight:500"}">${label}</span>
    <span style="font-size:11px;font-weight:700;color:${bold ? "#fff" : "#e5e7eb"};white-space:nowrap">${value}</span>
  </div>`;
}

function tooltipDivider() {
  return `<div style="border-top:1px solid #374151;margin:5px 0"></div>`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  height = 300,
  className = "",
  currency = "USD",
  currencySymbol = "$",
}) => {
  const chartRef = useRef<ChartInstance<"line"> | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);
  const lastTooltipIndexRef = useRef<number>(-1);
  const spendLabel = `Spend (${currency})`;
  const { isDark } = useTheme();
  const colors = useMemo(() => getChartColors(isDark), [isDark]);

  // ── External tooltip ──────────────────────────────────────────────────────

  const buildTooltip = useCallback(
    (index: number) => {
      const point = data[index];
      if (!point) return "";

      const spend = point.spend_usd ?? 0;
      const impressions = point.impressions ?? 0;
      const clicks = point.clicks ?? 0;
      const pointCtr =
        impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : "0.00";

      return `
        ${tooltipRow(spendLabel, `${currencySymbol}${spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, colors.spend, true)}
        ${tooltipDivider()}
        ${tooltipRow("Impressions", impressions.toLocaleString(), colors.installs, true)}
        ${tooltipRow("Clicks", clicks.toLocaleString(), colors.clicks, true)}
        ${tooltipDivider()}
        ${tooltipRow("CTR", `${pointCtr}%`, colors.axisText)}
      `;
    },
    [data, spendLabel, currencySymbol]
  );

  const externalTooltip = useCallback(
    (context: { chart: ChartInstance; tooltip: TooltipModel<"line"> }) => {
      const { chart, tooltip } = context;
      const tooltipEl = tooltipElRef.current;
      if (!tooltipEl) return;

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = "0";
        tooltipEl.style.transform = "translateY(6px) scale(0.97)";
        lastTooltipIndexRef.current = -1;
        return;
      }

      const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;

      // Only rebuild HTML when the hovered data point changes
      if (index !== lastTooltipIndexRef.current) {
        const label = tooltip.dataPoints?.[0]?.label ?? "";
        const dateStr = (() => {
          if (!label) return "";
          const datePart = label.includes("T") ? label.split("T")[0] : label;
          const parts = datePart.split("-");
          if (parts.length === 3) {
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            return `${parts[2]} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
          }
          return label;
        })();

        tooltipEl.innerHTML = `
          <div style="font-size:12px;font-weight:700;color:#fff;margin-bottom:8px">${dateStr}</div>
          <div style="display:flex;flex-direction:column;gap:4px">
            ${buildTooltip(index)}
          </div>
        `;
        lastTooltipIndexRef.current = index;
      }

      // Reposition synchronously — no rAF delay
      const canvasRect = chart.canvas.getBoundingClientRect();
      const x = canvasRect.left + tooltip.caretX;
      const y = canvasRect.top + tooltip.caretY;

      const tooltipWidth = tooltipEl.offsetWidth || 220;
      const tooltipHeight = tooltipEl.offsetHeight || 160;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const left = x + tooltipWidth + 16 > viewportWidth ? x - tooltipWidth - 16 : x + 16;
      const top = Math.max(8, Math.min(y - tooltipHeight / 2, viewportHeight - tooltipHeight - 8));

      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.top = `${top}px`;
      tooltipEl.style.opacity = "1";
      tooltipEl.style.transform = "translateY(0) scale(1)";
    },
    [buildTooltip]
  );

  // ── Chart config ──────────────────────────────────────────────────────────

  const chartData: ChartData<"line"> = {
    labels: data.map((d) => d.date),
    datasets: [
      {
        label: spendLabel,
        data: data.map((d) => d.spend_usd),
        borderColor: colors.spend,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        yAxisID: "y",
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: "Impressions",
        data: data.map((d) => d.impressions),
        borderColor: colors.installs,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        yAxisID: "y1",
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: "Clicks",
        data: data.map((d) => d.clicks),
        borderColor: colors.clicks,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        yAxisID: "y1",
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12, weight: "bold" },
          color: colors.legendText,
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 12,
          padding: 20,
        },
      },
      tooltip: {
        enabled: false,
        external: externalTooltip,
      },
      zoom: {
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
          font: { size: 11 },
          color: colors.axisText,
          callback: function (_value, index) {
            const label = this.getLabelForValue(index);
            if (label && typeof label === "string") {
              const datePart = label.includes("T") ? label.split("T")[0] : label;
              const parts = datePart.split("-");
              if (parts.length === 3) {
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                return `${parts[2]} ${months[parseInt(parts[1]) - 1]}`;
              }
            }
            return label;
          },
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        grid: { display: false },
        border: { display: false },
        title: {
          display: true,
          text: `Spend (${currency})`,
          color: colors.spend,
          font: { size: 11, weight: "bold" },
          padding: { bottom: 6 },
        },
        ticks: {
          font: { size: 11 },
          color: colors.axisText,
          callback: (v) =>
            typeof v === "number"
              ? `${currencySymbol}${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : v,
        },
      },
      y1: {
        type: "linear" as const,
        display: true,
        position: "right" as const,
        grid: { drawOnChartArea: true, color: colors.grid },
        title: {
          display: true,
          text: "Impressions / Clicks",
          color: colors.installs,
          font: { size: 11, weight: "bold" },
          padding: { bottom: 6 },
        },
        border: { display: false },
        ticks: {
          font: { size: 11 },
          color: colors.axisText,
          callback: (v) =>
            typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v,
        },
      },
    },
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const gradientDefs: [string, string][] = [
      colors.gradients.spend,
      colors.gradients.revenue,
      colors.gradients.profit,
    ];
    chart.data.datasets.forEach((dataset, i) => {
      const [start, end] = gradientDefs[i];
      const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      g.addColorStop(0, start);
      g.addColorStop(1, end);
      dataset.backgroundColor = g;
    });
    chart.update();
  }, [data, isDark, colors]);

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* External tooltip */}
      <div
        ref={tooltipElRef}
        style={{
          position: "fixed",
          background: colors.tooltipBg,
          border: `1px solid ${colors.tooltipBorder}`,
          borderRadius: "10px",
          padding: "12px 14px",
          pointerEvents: "none",
          transition: "opacity 0.18s ease, transform 0.18s cubic-bezier(0.34,1.56,0.64,1)",
          opacity: 0,
          transform: "translateY(6px) scale(0.97)",
          zIndex: 9999,
          minWidth: "200px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          willChange: "transform, opacity",
        }}
      />

      {/* Chart */}
      <div style={{ height }}>
        <Line key={isDark ? "dark" : "light"} ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
};

export default PerformanceChart;
